import type { AqsiGoodCard, AqsiGoodEditRequest, GoodDraft } from './types'

/**
 * Предмет расчёта (тег 1212) и способ расчёта (тег 1214) словами; ставка НДС в кабинете — только код без названия, поэтому
 * показывается кодом. Неизвестный код показывается как есть, а не заменяется похожим словом.
 */
export const SUBJECT_LABELS: Readonly<Record<number, string>> = {
  1: 'Товар',
  2: 'Подакцизный товар',
  3: 'Работа',
  4: 'Услуга',
  5: 'Ставка азартной игры',
  6: 'Выигрыш азартной игры',
  7: 'Лотерейный билет',
  8: 'Выигрыш лотереи',
  9: 'Предоставление РИД',
  10: 'Платёж',
  11: 'Агентское вознаграждение',
  12: 'Выплата',
  13: 'Иной предмет расчёта',
  14: 'Имущественное право',
  15: 'Внереализационный доход',
  16: 'Иные платежи и взносы',
  17: 'Торговый сбор',
  18: 'Курортный сбор',
  19: 'Залог',
  20: 'Расход',
  30: 'Подакцизный товар с маркировкой, без кода',
  31: 'Подакцизный товар с маркировкой, с кодом',
  32: 'Товар с маркировкой, без кода',
  33: 'Товар с маркировкой, с кодом',
}

export const PAYMENT_METHOD_LABELS: Readonly<Record<number, string>> = {
  1: 'Предоплата 100 %',
  2: 'Частичная предоплата',
  3: 'Аванс',
  4: 'Полный расчёт',
  5: 'Частичный расчёт и кредит',
  6: 'Передача в кредит',
  7: 'Оплата кредита',
}

export const MAX_BARCODES = 4
export const MAX_PRICE = 9_999_999.99

const text = (value: number | null): string => (value === null ? '' : String(value))

/** Цена кабинета в виде для поля: 100 даёт «100.00». */
export function priceText(price: number | null): string {
  return price === null ? '' : price.toFixed(2)
}

export function draftOf(card: AqsiGoodCard): GoodDraft {
  return {
    name: card.name,
    sku: card.sku ?? '',
    price: priceText(card.price ?? 0),
    barcodes: card.barcodes,
    newBarcode: '',
    tax: text(card.tax),
    subject: text(card.subject),
    unit: card.unit ?? '',
    unitCode: text(card.unitCode),
    paymentMethodType: text(card.paymentMethodType),
    isWeight: card.isWeight ?? false,
    properties: (card.customProperties ?? []).filter((property) => !card.ownedPropertyKeys.includes(property.key)),
  }
}

const sameList = (left: readonly string[], right: readonly string[]) =>
  left.length === right.length && left.every((item, index) => item === right[index])

/** Есть ли несохранённая правка: новое поле штрихкода без «Добавить» тоже считается. */
export function isDirty(card: AqsiGoodCard, draft: GoodDraft): boolean {
  const base = draftOf(card)
  return (
    draft.newBarcode.trim() !== '' ||
    draft.name !== base.name ||
    draft.sku !== base.sku ||
    draft.price !== base.price ||
    !sameList(draft.barcodes, base.barcodes) ||
    draft.tax !== base.tax ||
    draft.subject !== base.subject ||
    draft.unit !== base.unit ||
    draft.unitCode !== base.unitCode ||
    draft.paymentMethodType !== base.paymentMethodType ||
    draft.isWeight !== base.isWeight ||
    draft.properties.length !== base.properties.length ||
    draft.properties.some(
      (property, index) => property.key !== base.properties[index]?.key || property.value !== base.properties[index]?.value,
    )
  )
}

const integer = (value: string): number | null => (/^\d{1,9}$/.test(value.trim()) ? Number.parseInt(value.trim(), 10) : null)

/** Цена: до двух знаков, запятая или точка. `null` — не цена. */
export function parsePrice(value: string): number | null {
  const normalized = value.trim().replace(',', '.')
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(normalized)) return null
  const price = Number(normalized)
  return price <= MAX_PRICE ? price : null
}

export interface GoodProblems {
  name?: string
  sku?: string
  price?: string
  barcodes?: string
  tax?: string
  subject?: string
  unit?: string
  unitCode?: string
  paymentMethodType?: string
  properties?: string
}

/** Проверка до отправки — те же границы, что у службы; допустимость кодов решает кабинет, его отказ придёт дословно. */
export function problemsOf(draft: GoodDraft): GoodProblems {
  const problems: GoodProblems = {}
  if (draft.name.trim() === '' || draft.name.trim().length > 128) problems.name = 'Наименование — от 1 до 128 символов'
  if (draft.sku.length > 64) problems.sku = 'Артикул — не длиннее 64 символов'
  if (parsePrice(draft.price) === null) problems.price = 'Цена — от 0 до 9 999 999,99, не более двух знаков после запятой'
  if (draft.barcodes.length > MAX_BARCODES || draft.barcodes.some((code) => !/^\d{4,22}$/.test(code)))
    problems.barcodes = 'Штрихкоды — до четырёх разных, из 4–22 цифр'
  const tax = integer(draft.tax)
  if (tax === null || tax < 1 || tax > 10) problems.tax = 'Код ставки НДС — число от 1 до 10'
  const subject = integer(draft.subject)
  if (subject === null || subject < 1 || subject > 33) problems.subject = 'Предмет расчёта — код от 1 до 33'
  if (draft.unit.trim() === '' || draft.unit.length > 64) problems.unit = 'Единица — от 1 до 64 символов'
  const unitCode = integer(draft.unitCode)
  if (unitCode === null || unitCode > 9999) problems.unitCode = 'Код меры — число от 0 до 9999'
  const method = integer(draft.paymentMethodType)
  if (method === null || method < 1 || method > 7) problems.paymentMethodType = 'Способ расчёта — код от 1 до 7'
  if (unitCode !== null && [10, 11, 12].includes(unitCode) !== draft.isWeight)
    problems.unitCode = 'Весовой признак должен совпадать с кодом меры: 10, 11 и 12 — весовые'
  const keys = draft.properties.map((property) => property.key.trim())
  if (
    keys.some((key) => key === '' || key.length > 256) ||
    new Set(keys).size !== keys.length ||
    draft.properties.some((property) => property.value.length > 1024)
  )
    problems.properties = 'У каждого свойства свой непустой ключ; значение — до 1024 символов'
  return problems
}

export const hasProblems = (problems: GoodProblems): boolean => Object.keys(problems).length > 0

/** Тело правки из черновика; `null`, если есть проблемы или нет ревизии. */
export function requestOf(card: AqsiGoodCard, draft: GoodDraft): AqsiGoodEditRequest | null {
  if (card.updatedAt === null || hasProblems(problemsOf(draft))) return null
  return {
    expectedUpdatedAt: card.updatedAt,
    name: draft.name.trim(),
    sku: draft.sku.trim() === '' ? null : draft.sku.trim(),
    price: parsePrice(draft.price) ?? 0,
    barcodes: draft.barcodes,
    tax: integer(draft.tax) ?? 0,
    subject: integer(draft.subject) ?? 0,
    unit: draft.unit.trim(),
    unitCode: integer(draft.unitCode) ?? 0,
    paymentMethodType: integer(draft.paymentMethodType) ?? 0,
    isWeight: draft.isWeight,
    ...(card.customProperties === null
      ? {}
      : { customProperties: draft.properties.map((property) => ({ key: property.key.trim(), value: property.value })) }),
  }
}

/** Добавить штрихкод из поля ввода: пусто, повтор или лишний — не добавляется. */
export function withBarcode(draft: GoodDraft): GoodDraft {
  const code = draft.newBarcode.trim()
  if (code === '' || draft.barcodes.includes(code) || draft.barcodes.length >= MAX_BARCODES) return draft
  return { ...draft, barcodes: [...draft.barcodes, code], newBarcode: '' }
}
